"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { TextLinkCTA } from "~/components/siteglue/siteglue-cta";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";
import { cn } from "~/lib/utils";

const FAQS = [
  {
    q: "What is a SiteGlue AI agent?",
    a: "A SiteGlue AI agent answers calls and website visitors, captures or schedules appointments when a calendar is connected, and routes the conversation to your Lead Team when a person is needed.",
  },
  {
    q: "Will SiteGlue replace my staff?",
    a: "No. SiteGlue is built so your SiteGlue Agent works alongside people. Your team can take over any conversation with Team Takeover™.",
  },
  {
    q: "Who is Trish?",
    a: "Trish is the SiteGlue onboarding guide. She sets up your AI agent from your website and the channels you choose.",
  },
  {
    q: "What does Human-in-the-Loop™ mean?",
    a: "The agent finishes many requests. A person on your Lead Team steps in when needed or when asked. That handoff is Team Takeover™.",
  },
  {
    q: "How fast can I get started?",
    a: "Share your website and Trish walks through setup one step at a time. Every plan includes a 15-day trial. A card is required up front and the plan price starts on day 15.",
  },
  {
    q: "Is this an AI agent for a dental office or another business?",
    a: "Yes. People looking for an AI agent for a dental office, a PT clinic, or another local business start the same way: choose a role template and Trish builds that employee from the website.",
  },
  {
    q: "Do I need developers?",
    a: "No. Standard website chat is one line of code. If you want phone, SMS, or a custom connection, book a strategy call and a person designs it with you.",
  },
] as const;

export function SiteGlueHomeFaq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <HomeSection id="faq" tone="white">
      <FadeIn className="mx-auto max-w-2xl text-center">
        <HomeEyebrow>FAQ</HomeEyebrow>
        <HomeHeading className="mx-auto">Questions teams ask before hiring.</HomeHeading>
        <HomeLead className="mx-auto">
          Straight answers about SiteGlue Agent, Trish, and keeping humans in the loop.
        </HomeLead>
      </FadeIn>

      <div className="mx-auto mt-12 max-w-2xl divide-y divide-slate-200 border-y border-slate-200">
        {FAQS.map((item, i) => {
          const isOpen = open === i;
          return (
            <FadeIn key={item.q} delay={0.03 * i}>
              <div>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span className="text-sm font-semibold text-slate-900 sm:text-base">{item.q}</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-slate-400 transition",
                      isOpen && "rotate-180 text-[#4f81bd]",
                    )}
                    aria-hidden
                  />
                </button>
                {isOpen ? (
                  <p className="pb-5 text-sm leading-relaxed text-slate-600">{item.a}</p>
                ) : null}
              </div>
            </FadeIn>
          );
        })}
      </div>

      <FadeIn className="mt-8 text-center">
        <TextLinkCTA action="discoverPlatform" href="/faq" location="feature_section" label="View all FAQs" />
      </FadeIn>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />
    </HomeSection>
  );
}
